/**
 * NotificationBell — bell icon + unread badge + dropdown panel.
 *
 * Rendered inside `AppNav.jsx` between the primary nav pill row and
 * the user menu. Feature-flag hidden entirely when
 * `notifications_v1` is off.
 *
 * Polling: `GET /api/notifications/unread-count` every 60 seconds
 * (setInterval, cleared on unmount). Also refetches on:
 *   - dropdown open
 *   - route navigation events (via `useLocation`)
 *   - manual `refresh()` triggered by dropdown actions
 *
 * Optimistic UX rules (per user CP2 brief):
 *   - Mark-read POST failure on row click: silent — do NOT block
 *     navigation. `_readRetryQueue` tracks failed IDs; next dropdown
 *     open retries them once.
 *   - Mark-all-read POST failure: subtle inline banner at the top of
 *     the dropdown, dismissible.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bell, X, Loader2 } from "lucide-react";
import { useFeatureFlags } from "@/feature-flags/FeatureFlagsContext";
import { api } from "@/api/client";
import NotificationDropdownPanel from "./NotificationDropdownPanel";
import { formatBadgeCount } from "./utils";

const POLL_INTERVAL_MS = 60_000;

export default function NotificationBell() {
  const { isEnabled } = useFeatureFlags();
  const enabled = isEnabled("notifications_v1");

  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [markAllError, setMarkAllError] = useState(null);
  const [markAllInFlight, setMarkAllInFlight] = useState(false);

  // Mark-read retry queue — populated when a row-click mark-read
  // POST fails silently. Next dropdown open re-fires them once.
  const readRetryQueue = useRef(new Set());
  const dropdownRef = useRef(null);
  const location = useLocation();

  const refreshCount = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await api.get("/notifications/unread-count");
      setUnreadCount(res.data?.unread_count ?? 0);
    } catch (_err) {
      // Silent — bell shouldn't spam the console on transient blips.
    }
  }, [enabled]);

  const refreshList = useCallback(async () => {
    if (!enabled) return;
    setLoadingList(true);
    try {
      const res = await api.get("/notifications", {
        params: { filter: "all", limit: 10, offset: 0 },
      });
      setItems(res.data?.items || []);
      setUnreadCount(res.data?.unread_count ?? 0);
    } catch (_err) {
      // Silent
    } finally {
      setLoadingList(false);
    }
  }, [enabled]);

  // 60s polling. Clear on unmount / disable.
  useEffect(() => {
    if (!enabled) return undefined;
    refreshCount();
    const interval = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, refreshCount]);

  // Refetch on route change AND on a custom "notifications:changed"
  // window event (dispatched from `/notifications` after any
  // mutation so the bell badge syncs immediately without waiting
  // for the 60s poll cycle).
  useEffect(() => {
    refreshCount();
  }, [location.pathname, refreshCount]);

  useEffect(() => {
    const onChanged = () => refreshCount();
    window.addEventListener("notifications:changed", onChanged);
    return () => window.removeEventListener("notifications:changed", onChanged);
  }, [refreshCount]);

  // Refetch list contents when dropdown opens; also flush the
  // mark-read retry queue (per optimistic-UX resilience rule).
  useEffect(() => {
    if (!open) return;
    refreshList();
    if (readRetryQueue.current.size > 0) {
      const ids = Array.from(readRetryQueue.current);
      readRetryQueue.current.clear();
      ids.forEach((id) => {
        // Fire-and-forget; on failure, keep them out of the queue —
        // we only retry once per user brief.
        api.post(`/notifications/${id}/read`).catch(() => {});
      });
    }
  }, [open, refreshList]);

  // Outside-click closes the panel.
  useEffect(() => {
    if (!open) return undefined;
    function onClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  async function handleMarkRead(notification) {
    if (notification.is_read) return;
    // Optimistic local flip.
    setItems((cur) =>
      cur.map((n) =>
        n.id === notification.id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n,
      ),
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await api.post(`/notifications/${notification.id}/read`);
      window.dispatchEvent(new Event("notifications:changed"));
    } catch (_err) {
      // Silent per CP2 brief — queue for one-time retry on next open.
      readRetryQueue.current.add(notification.id);
    }
  }

  async function handleMarkAllRead() {
    if (markAllInFlight) return;
    setMarkAllError(null);
    setMarkAllInFlight(true);
    // Optimistic local flip.
    setItems((cur) => cur.map((n) => ({ ...n, is_read: true, read_at: n.read_at || new Date().toISOString() })));
    setUnreadCount(0);
    try {
      await api.post("/notifications/read-all");
      window.dispatchEvent(new Event("notifications:changed"));
    } catch (_err) {
      setMarkAllError(
        "Some notifications couldn't be marked read. Try again in a moment.",
      );
      // Reconcile — server state is authoritative on next refresh.
      refreshList();
    } finally {
      setMarkAllInFlight(false);
    }
  }

  function handleNavigateClose() {
    setOpen(false);
  }

  if (!enabled) return null;

  const badgeCopy = formatBadgeCount(unreadCount);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        data-testid="app-nav-notifications-bell"
        data-unread-count={unreadCount}
        onClick={() => setOpen((v) => !v)}
        title={unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications"}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
        className="relative flex items-center justify-center w-9 h-9 rounded-full border border-v2-border text-v2-textMuted hover:text-v2-text hover:bg-v2-surfaceSubtle transition-colors"
      >
        <Bell className="w-4 h-4" />
        {badgeCopy && (
          <span
            data-testid="app-nav-notifications-bell-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center text-white"
            style={{ background: "var(--v2-accent-strong)" }}
          >
            {badgeCopy}
          </span>
        )}
      </button>

      {open && (
        <NotificationDropdownPanel
          items={items}
          loading={loadingList}
          unreadCount={unreadCount}
          onMarkRead={handleMarkRead}
          onMarkAllRead={handleMarkAllRead}
          onMarkAllRefreshError={markAllError}
          onDismissMarkAllError={() => setMarkAllError(null)}
          markAllInFlight={markAllInFlight}
          onNavigateClose={handleNavigateClose}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
